import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import Select from "react-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormModal } from "@/components/Settings/FormModal";
import { organizationsApi, usersApi } from "@/apis";
import type { Organization, UserItem } from "@/apis";

interface UserFormState {
  email: string;
  groups: string[];
  first_name: string;
  last_name: string;
  temporary_password: string;
  organization_id: string;
}

const EMPTY_USER_FORM: UserFormState = {
  email: "",
  groups: [],
  first_name: "",
  last_name: "",
  temporary_password: "",
  organization_id: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function UsersManager() {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [modalError, setModalError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState<UserItem | null>(null);
  const [form, setForm] = useState<UserFormState>(EMPTY_USER_FORM);
  const [groupOptions, setGroupOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const modalTitle = useMemo(() => (editing ? "Edit User" : "Create User"), [editing]);

  const loadUsers = async (pageToLoad: number = 1, overrideLimit?: number) => {
    setLoading(true);
    setPageError("");
    try {
      const pageSize = overrideLimit ?? limit;
      const data = await usersApi.list({ page: pageToLoad, limit: pageSize });
      setUsers(data.items ?? []);
      setTotalCount(data.total_count ?? 0);
      setCurrentPage(data.page ?? pageToLoad);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to load users");
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  useEffect(() => {
    const loadGroupOptions = async () => {
      try {
        const groups = await usersApi.cognitoGroups();
        setGroupOptions(
          groups.map((group) => ({
            value: group,
            label: group,
          }))
        );
      } catch {
        setGroupOptions([]);
      }
    };
    void loadGroupOptions();
  }, []);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const data = await organizationsApi.list({ page: 1, limit: 500 });
        setOrganizations(data.items ?? []);
      } catch {
        setOrganizations([]);
      }
    };
    void loadOrganizations();
  }, []);

  const organizationOptions = useMemo(
    () =>
      organizations.map((org) => ({
        value: org.organizationId,
        label: org.organizationName,
      })),
    [organizations]
  );

  const organizationNameById = useMemo(() => {
    const map = new Map<string, string>();
    organizations.forEach((org) => map.set(org.organizationId, org.organizationName));
    return map;
  }, [organizations]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_USER_FORM);
    setModalError("");
    setIsModalOpen(true);
  };

  const openEdit = (user: UserItem) => {
    setEditing(user);
    setForm({
      email: user.email,
      groups: user.groups || [],
      first_name: "",
      last_name: "",
      temporary_password: "",
      organization_id: user.organization_id ?? "",
    });
    setModalError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalError("");
    setIsModalOpen(false);
  };

  const saveUser = async () => {
    const normalizedEmail = form.email.trim().toLowerCase();
    const normalizedFirstName = form.first_name.trim();
    const normalizedLastName = form.last_name.trim();
    const normalizedTemporaryPassword = form.temporary_password.trim();

    if (!normalizedEmail) {
      setModalError("Email is required.");
      return;
    }
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setModalError("Email must be a valid email address.");
      return;
    }
    if (!form.groups.length) {
      setModalError("At least one Group is required.");
      return;
    }
    if (!form.organization_id) {
      setModalError("Organization is required.");
      return;
    }
    if (!editing) {
      if (!normalizedFirstName) {
        setModalError("First Name is required.");
        return;
      }
      if (!normalizedLastName) {
        setModalError("Last Name is required.");
        return;
      }
      if (!normalizedTemporaryPassword) {
        setModalError("Temporary Password is required.");
        return;
      }
      if (normalizedTemporaryPassword.length < 8) {
        setModalError("Temporary Password must be at least 8 characters.");
        return;
      }
    }

    setIsSaving(true);
    setModalError("");
    try {
      if (editing) {
        await usersApi.update(editing.user_id, {
          groups: form.groups,
          organization_id: form.organization_id,
        });
      } else {
        await usersApi.create({
          email: normalizedEmail,
          groups: form.groups,
          first_name: normalizedFirstName,
          last_name: normalizedLastName,
          temporary_password: normalizedTemporaryPassword,
          organization_id: form.organization_id,
        });
      }
      setIsModalOpen(false);
      await loadUsers();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Failed to save user");
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteConfirm = (user: UserItem) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    setPageError("");
    try {
      await usersApi.remove(userToDelete.user_id);
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      await loadUsers(currentPage, limit);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / limit)), [totalCount, limit]);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5];
    }
    if (currentPage >= totalPages - 2) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  }, [currentPage, totalPages]);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      void loadUsers(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      void loadUsers(currentPage + 1);
    }
  };

  const handleLimitChange = (selected: { value: number; label: string } | null) => {
    const nextLimit = selected?.value ?? 25;
    setLimit(nextLimit);
    void loadUsers(1, nextLimit);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl">Users</CardTitle>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {pageError ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {pageError}
          </div>
        ) : null}

        <div className="relative h-[520px] overflow-auto rounded-md border">
          <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                  <th className="px-3 py-2 text-left font-medium">Organization</th>
                  <th className="px-3 py-2 text-left font-medium">Groups</th>
                  <th className="px-3 py-2 text-left font-medium">Roles</th>
                  <th className="px-3 py-2 text-left font-medium">Permissions</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-sm text-muted-foreground" colSpan={6}>
                      {loading ? "" : "No users found."}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                  <tr key={user.user_id} className="border-t">
                    <td className="px-3 py-2">{user.email}</td>
                    <td className="px-3 py-2">
                      {user.organization_id
                        ? organizationNameById.get(user.organization_id) || user.organization_id
                        : "-"}
                    </td>
                    <td className="px-3 py-2">{(user.groups || []).join(", ") || "-"}</td>
                    <td className="px-3 py-2">{(user.roles || []).join(", ") || "-"}</td>
                    <td className="px-3 py-2">{(user.permissions || []).join(", ") || "-"}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(user)}>
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteConfirm(user)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
          </table>
          {loading ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Limit</span>
            <Select
              className="react-select-container min-w-[120px]"
              classNamePrefix="react-select"
              value={{ value: limit, label: `${limit}` }}
              onChange={(selected) =>
                handleLimitChange(selected as { value: number; label: string } | null)
              }
              options={[
                { value: 25, label: "25" },
                { value: 50, label: "50" },
                { value: 100, label: "100" },
                { value: 200, label: "200" },
                { value: 500, label: "500" },
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({totalCount} total users)
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={loading || currentPage === 1}
            >
              Previous
            </Button>
            {visiblePageNumbers.map((pageNumber) => (
              <Button
                key={pageNumber}
                variant={pageNumber === currentPage ? "default" : "outline"}
                size="sm"
                onClick={() => void loadUsers(pageNumber)}
                disabled={loading}
              >
                {pageNumber}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={loading || currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>

      <FormModal
        title={modalTitle}
        description="Manage users, groups, roles, and permissions."
        isOpen={isModalOpen}
        isSaving={isSaving}
        onClose={closeModal}
        onSubmit={saveUser}
      >
        {modalError ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {modalError}
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Email *</label>
            <Input
              value={form.email}
              disabled={!!editing}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="user@example.com"
            />
          </div>

          {!editing ? (
            <>
              <div className="space-y-1">
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Last Name *</label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Doe"
                />
              </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Temporary Password *</label>
              <Input
                type="password"
                value={form.temporary_password}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, temporary_password: e.target.value }))
                }
                placeholder="Minimum 8 characters"
              />
            </div>
            </>
          ) : null}

          <div className="space-y-1">
            <label className="text-sm font-medium">Organization *</label>
            <Select
              options={organizationOptions}
              value={
                organizationOptions.find((opt) => opt.value === form.organization_id) ?? null
              }
              onChange={(selected) =>
                setForm((prev) => ({
                  ...prev,
                  organization_id:
                    selected && !Array.isArray(selected)
                      ? (selected as { value: string; label: string }).value
                      : "",
                }))
              }
              placeholder="Select an organization"
              isClearable
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Groups *</label>
            <Select
              isMulti
              options={groupOptions}
              value={groupOptions.filter((opt) => form.groups.includes(opt.value))}
              onChange={(selected) =>
                setForm((prev) => ({
                  ...prev,
                  groups: Array.isArray(selected) ? selected.map((opt) => opt.value) : [],
                }))
              }
              placeholder="Select one or more groups"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        </div>
      </FormModal>

      {deleteConfirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle>Delete User</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{userToDelete?.email}</strong>? This action
                cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={closeDeleteConfirm} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </Card>
  );
}

